import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Gift,
  Tag,
  Calendar,
  X,
  Save,
  AlertCircle,
  CheckCircle,
  Package,
} from "lucide-react";
import { API_BASE_URL } from "../../utils/apiConfig";
import { useLanguage } from "../../contexts/LanguageContext";

interface Offer {
  _id?: string;
  id?: string;
  name: string;
  type: "special" | "gift";
  description?: string;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  unlimited: boolean;
  // Special Offer fields
  availableProducts?: Array<{
    productId: string;
    productName: string;
    price: number;
  }>;
  maxPickCount?: number; // n products
  specialPrice?: number;
  // Gift Offer fields
  baseProducts?: Array<{
    productId: string;
    productName: string;
    type: "single" | "bundle";
  }>;
  giftItems?: Array<{
    productId: string;
    productName: string;
    quantity: number;
  }>;
  freeShipping?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface Product {
  _id: string;
  product_name: string;
  price_rsd?: number;
  price_mkd?: number;
  type?: "single" | "bundle";
}

const Offers: React.FC = () => {
  const { t } = useLanguage();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<Offer>({
    name: "",
    type: "special",
    description: "",
    isActive: true,
    unlimited: false,
    startDate: "",
    endDate: "",
    availableProducts: [],
    maxPickCount: 1,
    specialPrice: 0,
    baseProducts: [],
    giftItems: [],
    freeShipping: false,
  });

  useEffect(() => {
    loadOffers();
    loadProducts();
  }, []);

  const loadOffers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/api/offers`, {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setOffers(data.data.offers || []);
      }
    } catch (err) {
      console.error("Error loading offers:", err);
      setError(err instanceof Error ? err.message : "Failed to load offers");
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/api/products?limit=1000`, {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setProducts(data.data.products || []);
      }
    } catch (err) {
      console.error("Error loading products:", err);
    }
  };

  const handleCreate = () => {
    setEditingOffer(null);
    setFormData({
      name: "",
      type: "special",
      description: "",
      isActive: true,
      unlimited: false,
      startDate: "",
      endDate: "",
      availableProducts: [],
      maxPickCount: 1,
      specialPrice: 0,
      baseProducts: [],
      giftItems: [],
      freeShipping: false,
    });
    setShowModal(true);
  };

  const handleEdit = (offer: Offer) => {
    setEditingOffer(offer);
    setFormData({
      ...offer,
      startDate: offer.startDate ? offer.startDate.split("T")[0] : "",
      endDate: offer.endDate ? offer.endDate.split("T")[0] : "",
    });
    setShowModal(true);
  };

  const handleDelete = async (offerId: string) => {
    if (!window.confirm("Are you sure you want to delete this offer?")) {
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/api/offers/${offerId}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        await loadOffers();
      } else {
        throw new Error(data.error || "Failed to delete offer");
      }
    } catch (err) {
      console.error("Error deleting offer:", err);
      alert(err instanceof Error ? err.message : "Failed to delete offer");
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
      setError("Offer name is required");
      return;
    }

    if (formData.type === "special") {
      if (!formData.availableProducts || formData.availableProducts.length === 0) {
        setError("Please select at least one product for special offer");
        return;
      }
      if (!formData.maxPickCount || formData.maxPickCount < 1) {
        setError("Please set the maximum number of products that can be picked");
        return;
      }
      if (!formData.specialPrice || formData.specialPrice <= 0) {
        setError("Please set a valid special price");
        return;
      }
    } else if (formData.type === "gift") {
      if (!formData.baseProducts || formData.baseProducts.length === 0) {
        setError("Please select at least one base product for gift offer");
        return;
      }
      if (!formData.giftItems || formData.giftItems.length === 0) {
        setError("Please add at least one gift item");
        return;
      }
    }

    if (!formData.unlimited && (!formData.startDate || !formData.endDate)) {
      setError("Please set start and end dates, or select unlimited");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem("authToken");
      const url = editingOffer
        ? `${API_BASE_URL}/api/offers/${editingOffer._id || editingOffer.id}`
        : `${API_BASE_URL}/api/offers`;
      const method = editingOffer ? "PUT" : "POST";

      const payload = {
        ...formData,
        startDate: formData.unlimited ? null : formData.startDate,
        endDate: formData.unlimited ? null : formData.endDate,
      };

      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to save offer");
      }

      const data = await response.json();
      if (data.success) {
        setShowModal(false);
        await loadOffers();
      } else {
        throw new Error(data.error || "Failed to save offer");
      }
    } catch (err) {
      console.error("Error saving offer:", err);
      setError(err instanceof Error ? err.message : "Failed to save offer");
    } finally {
      setSaving(false);
    }
  };

  const addAvailableProduct = (productId: string) => {
    const product = products.find((p) => p._id === productId);
    if (!product) return;

    const availableProducts = formData.availableProducts || [];
    if (availableProducts.some((p) => p.productId === productId)) {
      return; // Already added
    }

    setFormData({
      ...formData,
      availableProducts: [
        ...availableProducts,
        {
          productId,
          productName: product.product_name,
          price: product.price_rsd || product.price_mkd || 0,
        },
      ],
    });
  };

  const removeAvailableProduct = (productId: string) => {
    setFormData({
      ...formData,
      availableProducts: (formData.availableProducts || []).filter(
        (p) => p.productId !== productId
      ),
    });
  };

  const addBaseProduct = (productId: string) => {
    const product = products.find((p) => p._id === productId);
    if (!product) return;

    const baseProducts = formData.baseProducts || [];
    if (baseProducts.some((p) => p.productId === productId)) {
      return; // Already added
    }

    setFormData({
      ...formData,
      baseProducts: [
        ...baseProducts,
        {
          productId,
          productName: product.product_name,
          type: (product.type || "single") as "single" | "bundle",
        },
      ],
    });
  };

  const removeBaseProduct = (productId: string) => {
    setFormData({
      ...formData,
      baseProducts: (formData.baseProducts || []).filter(
        (p) => p.productId !== productId
      ),
    });
  };

  const addGiftItem = (productId: string, quantity: number = 1) => {
    const product = products.find((p) => p._id === productId);
    if (!product) return;

    const giftItems = formData.giftItems || [];
    const existingIndex = giftItems.findIndex((g) => g.productId === productId);

    if (existingIndex >= 0) {
      // Update quantity
      const updated = [...giftItems];
      updated[existingIndex] = {
        ...updated[existingIndex],
        quantity: updated[existingIndex].quantity + quantity,
      };
      setFormData({ ...formData, giftItems: updated });
    } else {
      // Add new
      setFormData({
        ...formData,
        giftItems: [
          ...giftItems,
          {
            productId,
            productName: product.product_name,
            quantity,
          },
        ],
      });
    }
  };

  const removeGiftItem = (productId: string) => {
    setFormData({
      ...formData,
      giftItems: (formData.giftItems || []).filter(
        (g) => g.productId !== productId
      ),
    });
  };

  const updateGiftItemQuantity = (productId: string, quantity: number) => {
    const giftItems = formData.giftItems || [];
    const updated = giftItems.map((g) =>
      g.productId === productId ? { ...g, quantity } : g
    );
    setFormData({ ...formData, giftItems: updated });
  };

  const calculateRegularPrice = () => {
    if (formData.type !== "special" || !formData.availableProducts) return 0;
    return formData.availableProducts.reduce((sum, p) => sum + p.price, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#264a3e]"></div>
        <span className="ml-2 text-gray-600">Loading offers...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Offers Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            Create and manage special offers and gift offers
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Offer
        </button>
      </div>

      {error && !showModal && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Offers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Period
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {offers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <Gift className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No offers created yet</p>
                  <button
                    onClick={handleCreate}
                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#264a3e] hover:bg-[#1e3a32]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Offer
                  </button>
                </td>
              </tr>
            ) : (
              offers.map((offer) => (
                <tr key={offer._id || offer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{offer.name}</div>
                    {offer.description && (
                      <div className="text-xs text-gray-500">{offer.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        offer.type === "special"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-purple-100 text-purple-800"
                      }`}
                    >
                      {offer.type === "special" ? (
                        <>
                          <Tag className="w-3 h-3 mr-1" />
                          Special Offer
                        </>
                      ) : (
                        <>
                          <Gift className="w-3 h-3 mr-1" />
                          Gift Offer
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        offer.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {offer.isActive ? (
                        <>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <X className="w-3 h-3 mr-1" />
                          Inactive
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {offer.unlimited ? (
                      <span className="text-gray-900 font-medium">Unlimited</span>
                    ) : (
                      <div>
                        <div>
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {offer.startDate
                            ? new Date(offer.startDate).toLocaleDateString()
                            : "—"}
                        </div>
                        <div className="text-xs">
                          to{" "}
                          {offer.endDate
                            ? new Date(offer.endDate).toLocaleDateString()
                            : "—"}
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {offer.type === "special" ? (
                      <div>
                        <div>
                          {offer.availableProducts?.length || 0} product(s) available
                        </div>
                        <div>
                          Pick {offer.maxPickCount} for{" "}
                          {offer.specialPrice?.toLocaleString()} RSD
                        </div>
                        <div className="text-xs text-gray-400">
                          Regular: {calculateRegularPrice().toLocaleString()} RSD
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div>
                          {offer.baseProducts?.length || 0} base product(s)
                        </div>
                        <div>
                          {offer.giftItems?.length || 0} gift item(s)
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(offer)}
                        className="text-[#264a3e] hover:text-[#1e3a32]"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(offer._id || offer.id || "")}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingOffer ? "Edit Offer" : "Create New Offer"}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900">Basic Information</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Offer Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                    placeholder="e.g., Special Summer Offer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                    placeholder="Optional description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Offer Type *
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="special"
                        checked={formData.type === "special"}
                        onChange={(e) =>
                          setFormData({ ...formData, type: "special" as "special" | "gift" })
                        }
                        className="mr-2"
                      />
                      <Tag className="w-4 h-4 mr-1 text-blue-600" />
                      <span>Special Offer</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="gift"
                        checked={formData.type === "gift"}
                        onChange={(e) =>
                          setFormData({ ...formData, type: "gift" as "special" | "gift" })
                        }
                        className="mr-2"
                      />
                      <Gift className="w-4 h-4 mr-1 text-purple-600" />
                      <span>Gift Offer</span>
                    </label>
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <label className="text-sm text-gray-700">Active</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.freeShipping ?? false}
                    onChange={(e) =>
                      setFormData({ ...formData, freeShipping: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <label className="text-sm text-gray-700">Free shipping</label>
                </div>
              </div>

              {/* Time Period */}
              <div className="space-y-4 border-t border-gray-200 pt-4">
                <h4 className="text-lg font-medium text-gray-900">Time Period</h4>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.unlimited}
                    onChange={(e) =>
                      setFormData({ ...formData, unlimited: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <label className="text-sm text-gray-700">Unlimited (no end date)</label>
                </div>

                {!formData.unlimited && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date *
                      </label>
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) =>
                          setFormData({ ...formData, startDate: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date *
                      </label>
                      <input
                        type="date"
                        value={formData.endDate}
                        onChange={(e) =>
                          setFormData({ ...formData, endDate: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Special Offer Configuration */}
              {formData.type === "special" && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <h4 className="text-lg font-medium text-gray-900">Special Offer Configuration</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Available Products *
                    </label>
                    <div className="mb-2">
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            addAvailableProduct(e.target.value);
                            e.target.value = "";
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      >
                        <option value="">Select a product to add...</option>
                        {products
                          .filter(
                            (p) =>
                              !formData.availableProducts?.some(
                                (ap) => ap.productId === p._id
                              )
                          )
                          .map((product) => (
                            <option key={product._id} value={product._id}>
                              {product.product_name} -{" "}
                              {(product.price_rsd || product.price_mkd || 0).toLocaleString()}{" "}
                              RSD
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      {formData.availableProducts?.map((product) => (
                        <div
                          key={product.productId}
                          className="flex items-center justify-between bg-gray-50 p-2 rounded"
                        >
                          <div>
                            <span className="text-sm font-medium">{product.productName}</span>
                            <span className="text-xs text-gray-500 ml-2">
                              {product.price.toLocaleString()} RSD
                            </span>
                          </div>
                          <button
                            onClick={() => removeAvailableProduct(product.productId)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    {formData.availableProducts && formData.availableProducts.length > 0 && (
                      <div className="mt-2 text-sm text-gray-600">
                        Regular Total: {calculateRegularPrice().toLocaleString()} RSD
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Pick Count (n) *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max={formData.availableProducts?.length || 1}
                        value={formData.maxPickCount || 1}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            maxPickCount: parseInt(e.target.value) || 1,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Maximum number of products customer can pick
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Special Price (RSD) *
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.specialPrice === undefined || formData.specialPrice === null ? "" : formData.specialPrice}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow empty string, otherwise parse the number
                          if (value === "") {
                            setFormData({
                              ...formData,
                              specialPrice: undefined,
                            });
                          } else {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue)) {
                              setFormData({
                                ...formData,
                                specialPrice: numValue,
                              });
                            }
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Price for {formData.maxPickCount || 1} selected product(s)
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Gift Offer Configuration */}
              {formData.type === "gift" && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  <h4 className="text-lg font-medium text-gray-900">Gift Offer Configuration</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Base Products (Single or Bundle) *
                    </label>
                    <div className="mb-2">
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            addBaseProduct(e.target.value);
                            e.target.value = "";
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      >
                        <option value="">Select a base product...</option>
                        {products
                          .filter(
                            (p) =>
                              !formData.baseProducts?.some(
                                (bp) => bp.productId === p._id
                              )
                          )
                          .map((product) => (
                            <option key={product._id} value={product._id}>
                              {product.product_name} (
                              {product.type === "bundle" ? "Bundle" : "Single"})
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      {formData.baseProducts?.map((product) => (
                        <div
                          key={product.productId}
                          className="flex items-center justify-between bg-gray-50 p-2 rounded"
                        >
                          <div>
                            <span className="text-sm font-medium">{product.productName}</span>
                            <span className="text-xs text-gray-500 ml-2">
                              ({product.type})
                            </span>
                          </div>
                          <button
                            onClick={() => removeBaseProduct(product.productId)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gift Items *
                    </label>
                    <div className="mb-2 flex space-x-2">
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            addGiftItem(e.target.value, 1);
                            e.target.value = "";
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      >
                        <option value="">Select a gift product...</option>
                        {products.map((product) => (
                          <option key={product._id} value={product._id}>
                            {product.product_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      {formData.giftItems?.map((gift) => (
                        <div
                          key={gift.productId}
                          className="flex items-center justify-between bg-purple-50 p-2 rounded"
                        >
                          <div className="flex items-center space-x-2">
                            <Gift className="w-4 h-4 text-purple-600" />
                            <span className="text-sm font-medium">{gift.productName}</span>
                            <input
                              type="number"
                              min="1"
                              value={gift.quantity}
                              onChange={(e) =>
                                updateGiftItemQuantity(
                                  gift.productId,
                                  parseInt(e.target.value) || 1
                                )
                              }
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                            <span className="text-xs text-gray-500">qty</span>
                          </div>
                          <button
                            onClick={() => removeGiftItem(gift.productId)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 border-t border-gray-200 pt-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-[#264a3e] text-white rounded-md text-sm font-medium hover:bg-[#1e3a32] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {editingOffer ? "Update Offer" : "Create Offer"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Offers;
